import { Controller } from '@hotwired/stimulus';

/**
 * validation-tracker-toggle — le BADGE de validation de l'en-tête du planning est l'interrupteur du bandeau de
 * suivi (refonte PR 2, Task 20.1, N-1 : maquette du 02/10 ; RV-35).
 *
 * Monté sur la racine `dom_id(plan, :validation)` de `shared/_execution_plan_validation` (agence seule), ancêtre
 * commun du badge (`trigger`) et du bandeau (`panel`, qui tient `Ui::VisaTrackerComponent`).
 *
 *   - Le serveur rend TOUJOURS replié : `aria-expanded="false"` sur le badge, attribut `hidden` sur le bandeau.
 *     Le bandeau ne porte aucune classe d'affichage (`flex`, `grid`…) : en Tailwind v4 elle battrait `hidden`.
 *   - Ce contrôleur n'écrit que des ATTRIBUTS (`aria-expanded`, `hidden`, `aria-label`, `title`) : le contour du badge
 *     affiché vient de ses variantes `aria-expanded:` (piège n° 3 : jamais de classe ni de style en JS).
 *   - L'état est mémorisé PAR NAVIGATEUR dans `localStorage[storageKey]` (N-1, précédent `jerico.gantt.legend.open`
 *     de la PR 1), lecture et écriture protégées (navigation privée, iframe cloisonnée : la bascule marche,
 *     sans mémoire). Il est réappliqué à la connexion (remplacement par `_host_rerender`) et après un MORPH
 *     (`_validation_refresh`, `method: :morph`) : idiomorph garde l'élément, `connect()` ne rejoue pas, et le
 *     rendu serveur replié a remis les attributs. `turbo:morph-element` remonte des descendants : seul l'hôte
 *     compte. Sans état mémorisé, le rendu serveur fait foi.
 *   - `anchor` : l'ancre `dom_id(plan, :validation_anchor)` que visent les lignes « Validation » de la Synthèse.
 *     Une page ouverte sur elle, un `hashchange` vers elle ou le clic d'un lien vers elle (hash déjà le bon,
 *     aucun `hashchange`) OUVRE le bandeau, et l'état ouvert est mémorisé.
 *
 * Targets : trigger (le badge `<button aria-expanded aria-controls>`), panel (le bandeau).
 * Values  : storageKey (String, vide = rien de mémorisé), anchor (String), showLabel / hideLabel (String :
 *           `aria-label` du badge replié / affiché, « Validé, afficher le suivi » ↔ « Validé, masquer le suivi »),
 *           showTitle / hideTitle (String : son infobulle `title`, planche Main, « Afficher le suivi de la
 *           validation » ↔ « Masquer le suivi de la validation »).
 * Actions : toggle (clic du badge), morphed (`turbo:morph-element`), hashChanged (`hashchange@window`),
 *           linkClicked (`click@document`).
 */
export default class extends Controller {
  static targets = ['trigger', 'panel'];

  static values = {
    storageKey: String,
    anchor: String,
    showLabel: String,
    hideLabel: String,
    showTitle: String,
    hideTitle: String,
  };

  connect() {
    const stored = this.readStoredState();
    if (stored !== null) this.apply(stored);
    this.openIfTargeted();
  }

  toggle() {
    const open = !this.isOpen();
    this.apply(open);
    this.writeStoredState(open);
  }

  // Le serveur re-rend replié : l'état mémorisé (un clic de l'utilisateur) l'emporte.
  morphed(event) {
    if (event?.target !== this.element) return;

    const stored = this.readStoredState();
    if (stored !== null) this.apply(stored);
  }

  hashChanged() {
    this.openIfTargeted();
  }

  linkClicked(event) {
    if (!this.anchorValue) return;

    const link = event?.target?.closest?.('a[href]');
    if (link?.getAttribute('href') === `#${this.anchorValue}`) this.open();
  }

  openIfTargeted() {
    if (this.anchorValue && window.location?.hash === `#${this.anchorValue}`) this.open();
  }

  open() {
    this.apply(true);
    this.writeStoredState(true);
  }

  isOpen() {
    return this.hasTriggerTarget && this.triggerTarget.getAttribute('aria-expanded') === 'true';
  }

  apply(open) {
    if (!this.hasTriggerTarget || !this.hasPanelTarget) return;

    this.triggerTarget.setAttribute('aria-expanded', open ? 'true' : 'false');
    this.panelTarget.hidden = !open;
    const label = open ? this.labelValue('hide') : this.labelValue('show');
    if (label) this.triggerTarget.setAttribute('aria-label', label);
    const title = open ? this.titleValue('hide') : this.titleValue('show');
    if (title) this.triggerTarget.setAttribute('title', title);
  }

  labelValue(state) {
    if (state === 'hide') return this.hasHideLabelValue ? this.hideLabelValue : '';
    return this.hasShowLabelValue ? this.showLabelValue : '';
  }

  titleValue(state) {
    if (state === 'hide') return this.hasHideTitleValue ? this.hideTitleValue : '';
    return this.hasShowTitleValue ? this.showTitleValue : '';
  }

  readStoredState() {
    if (!this.storageKeyValue) return null;

    try {
      const raw = window.localStorage.getItem(this.storageKeyValue);
      if (raw === 'true') return true;
      if (raw === 'false') return false;
    } catch {
      // stockage inaccessible : le rendu serveur fait foi
    }
    return null;
  }

  writeStoredState(open) {
    if (!this.storageKeyValue) return;

    try {
      window.localStorage.setItem(this.storageKeyValue, String(open));
    } catch {
      // stockage inaccessible : la bascule fonctionne, sans mémoire
    }
  }
}
